<template>
  <bean-source-page
    :type="type"
    :columns="columns"
    :form-columns="formColumns"
    :filter-columns="filterColumns"
    :actions="actions"
    :form="form"
    :title="$t('model.company')"
    resource="companies"
    :batch-actions="batchActions"
  />
</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import { filterColumnsExtend, columnsExtend, formColumnsExtend } from '../admin_grapes/companies.js';
import { getLabel } from '@/custom_form_columns_extend';
import _ from 'lodash';

@Component
export default class Companies extends Vue {
  @Prop(String) type;

  // form 新建表单初始化数据（default数据）
  form = {}

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - page type enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -RESTful api resource name
   */
  filterColumns() {
    const array = [
      'name_cont',
      {
        prop: 'date_range',
        label: '时间段',
        renderCell: {
          component: 'dateTimePicker',
          props: {
            type: 'daterange',
            rangeSeparator: '至',
            startPlaceholder: '开始日期',
            endPlaceholder: '结束日期',
            valueFormat: 'yyyy-MM-dd'
          }
        }
      }
    ].filter(Boolean)

    return filterColumnsExtend(array)
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - page type enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -RESTful api resource name
   */
  columns(h, { type }) {
    const abilities = _.get(this.$root.user, 'abilities', {});
    const array = [
      type === 'show' && 'id',
      'name',
      'jushuitan_supplier_id',
      type === 'show' && 'merchandisers',
      abilities.read_company_extra_info && {
        key: 'on_the_way_orders_count',
      },
      abilities.read_company_extra_info && {
        key: 'on_the_way_orders_cost',
      },
      {
        key: 'current_month_rating',
      },
      {
        key: 'current_quarter_rating',
      },
      {
        key: 'current_year_rating',
      },
      abilities.read_company_extra_info && {
        key: 'return_rate',
      },
      {
        key: 'overdue_rate',
      },
      {
        key: 'liquidated_damages',
      },
      {
        key: 'order_limit',
      },
      {
        key: 'order_usage_rate',
      },
      'created_at',
      'updated_at',
    ].filter(Boolean)

    return columnsExtend(array)
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - page type enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource - RESTful api resoure name
   * @param {Object} params.data - current form data
   * @param {Function} params.change - invoke this method to sync form change({ field: 'new value' })
   */
  formColumns(h, { data, type }) {
    const abilities = _.get(data, 'abilities', {
      edit_columns: true,
      edit_order_limit: true,
    });

    const array = [
      abilities.edit_columns && 'name',
      abilities.edit_columns && 'jushuitan_supplier_id',
      abilities.edit_order_limit && 'order_limit',
      abilities.edit_columns && {
        key: 'merchandiser_ids',
        renderCell: {
          component: 'select',
          props: {
            xRemotePreload: async () => {
              const { data } = await this.$request.get('users/read_options', { per_page: 100, role_eq: 'merchandiser' });
              return data.map(object => ({ value: object.id, label: getLabel({ item: object, v: { extra: { model_name: 'User' } } }) }));
            },
            xRemoteSearch: async (keyword) => {
              const { data } = await this.$request.get('users/read_options', { keyword, role_eq: 'merchandiser' });
              return data.map(object => ({ value: object.id, label: getLabel({ item: object, v: { extra: { model_name: 'User' } } }) }));
            }
          }
        }
      }
    ].filter(Boolean)

    return formColumnsExtend(array, { data: data, type: type })
  }

  /**
   * @param {Object} scope -页面/表格 scope 数据
   * @param {Object} scope.row - 详情页/表格行 数据
   * @param {Object} sourcePageScope
   * @param {Function} sourcePageScope.remove - sourcePage删除方法
   * @param {Function} sourcePageScope.reload - sourcePage重新请求页面数据
   */
  actions({ row }, { remove }) {
    return [
      row.abilities.update && {
        component: 'routeButton',
        props: {
          label: this.$t('edit'),
          to: { name: 'companies.edit', params: { id: row.id } },
          can: []
        }
      },
      {
        component: 'confirmButton',
        props: {
          label: this.$t('destroy'),
          type: 'danger',
          can: 'companies.destroy'
        },
        on: {
          confirm: () => remove(row.id)
        }
      }
    ].filter(Boolean);
  }

  /**
   * @param {Object} scope -表格 scope 数据
   * @param {Object} scope.selection - 表格已选择行数据
   * @param {Object} sourcePageScope
   * @param {Function} sourcePageScope.remove - sourcePage删除方法
   * @param {Function} sourcePageScope.reload - sourcePage重新请求页面数据
   */
  batchActions({ selection }) {
    const exportClick = async (selection) => {
      if (selection.length === 0) {
        await this.$request.get('/companies/export', { ...this.$route.query });
      } else {
        await this.$request.get('/companies/export', { ...this.$route.query, id_in: _.map(selection, item => item.id) });
      }

      this.$message.success('文件正在导出，导出成功后将会发送到邮箱');
    }

    return [
      () => <el-button type='primary' onClick={() => exportClick(selection)}>导出</el-button>
    ];
  }
}

export const route = [
  {
    path: '/companies',
    name: 'companies.index',
    component: Companies,
    props: {
      type: 'index',
    }
  },
  {
    path: '/companies/new',
    name: 'companies.new',
    component: Companies,
    props: {
      type: 'new',
    }
  },
  {
    path: '/companies/:id',
    name: 'companies.show',
    component: Companies,
    props: {
      type: 'show',
    }
  },
  {
    path: '/companies/:id/edit',
    name: 'companies.edit',
    component: Companies,
    props: {
      type: 'edit'
    }
  }
]
</script>
